"use client";

import type React from "react";
import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useStripe } from "../contexts/StripeContext";
import { useUsage } from "../contexts/UsageContext";
import { priceIds } from "../utils/constants";
import { HeroSection } from "../components/landing/HeroSection";
import type { StartClipping } from "../components/landing/HeroSection";
import { VideoShowcase } from "../components/landing/VideoShowcase";
import { HowItWorksModal } from "../components/landing/HowItWorksModal";
import { HowItWorksSection } from "../components/landing/HowItWorksSection";
import { CreateMillionsSection } from "../components/landing/CreateMillionsSection";
import { PricingSection } from "../components/landing/PricingSection";
import { ChatFAQSection } from "../components/landing/ChatFAQSection";
import { FinalCTASection } from "../components/landing/FinalCTASection";

interface LandingPageProps {
  onStartClipping: StartClipping;
}

export const LandingPage: React.FC<LandingPageProps> = ({
  onStartClipping,
}) => {
  const { createCheckoutSession, createPortalSession, checkUserSubscription } =
    useStripe();
  const { user } = useAuth();
  const { refreshUsage } = useUsage();
  const [isYearly, setIsYearly] = useState(true);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);

  const handleUpgrade = async (
    planName: string,
    isYearlyPlan = false,
    isQuarterly = false
  ) => {
    try {
      const selectedPriceId = isQuarterly
        ? priceIds[planName]?.quarterly
        : isYearlyPlan
        ? priceIds[planName]?.yearly
        : priceIds[planName]?.monthly;
      if (!selectedPriceId) {
        const billingType = isQuarterly
          ? "quarterly"
          : isYearlyPlan
          ? "yearly"
          : "monthly";
        throw new Error(
          `No price ID found for plan: ${planName} (${billingType})`
        );
      }

      if (user?.id) {
        const subscriptionStatus = await checkUserSubscription();
        if (subscriptionStatus.has_subscription) {
          await createPortalSession();
          return;
        }
      }
      await createCheckoutSession(selectedPriceId);
    } catch (error) {
      console.error("Error upgrading plan:", error);
      window.alert(
        `Failed to start upgrade process: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get(
      "session_id"
    );
    if (!sessionId || !user?.id) return;
    void refreshUsage(true).catch((error) => {
      if (process.env.NODE_ENV === "development") {
        console.warn("Failed to refresh usage after payment:", error);
      }
    });
  }, [user?.id, refreshUsage]);

  return (
    <main className="tc-landing min-h-screen bg-white">
      <HeroSection onStartClipping={onStartClipping} />
      <VideoShowcase />
      <CreateMillionsSection onStartClipping={onStartClipping} />
      <HowItWorksSection
        onOpenDetails={() => setShowHowItWorksModal(true)}
      />
      <div id="pricing-section">
        <PricingSection
          onStartClipping={onStartClipping}
          onUpgrade={handleUpgrade}
          isYearly={isYearly}
          setIsYearly={setIsYearly}
        />
      </div>
      <ChatFAQSection onStartClipping={onStartClipping} />
      <FinalCTASection onStartClipping={onStartClipping} />

      <HowItWorksModal
        isOpen={showHowItWorksModal}
        onClose={() => setShowHowItWorksModal(false)}
      />
    </main>
  );
};
